import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { CANCELLATION_FEEDBACK_REASONS } from '../constants/generatedCancellationFeedback';

export default function FeedbackForm() {
  const uid = new URLSearchParams(window.location.search).get('uid') || '';
  const [selected, setSelected] = useState(new Set());
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showDetailsError, setShowDetailsError] = useState(false);

  const canSubmit = selected.size > 0 && details.trim().length > 0;

  function toggleReason(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }

  async function submit() {
    if (details.trim().length === 0) {
      setShowDetailsError(true);
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'reviews'), {
        userId: uid,
        source: 'web_form',
        reasons: Array.from(selected),
        details: details.trim(),
        timestamp: serverTimestamp(),
      });
      setDone(true);
    } catch (e) {
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div style={s.page}>
        <div style={s.blob1} />
        <div style={s.blob2} />
        <div style={s.container}>
          <div style={s.logoWrap}>
            <img src="/logo.png" alt="Cura" style={s.logo} />
          </div>
          <div style={s.card}>
            <div style={s.doneWrap}>
              <div style={s.doneIconWrap}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <path d="M6 16L13 23L26 9" stroke="#24554C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h1 style={{ ...s.title, textAlign: 'center' }}>Thank you</h1>
              <p style={{ ...s.sub, textAlign: 'center', maxWidth: 320, margin: '0 auto' }}>
                Your feedback means a lot to us. We'll use it to make Cura better for everyone.
              </p>
              <p style={{ ...s.sub, textAlign: 'center', marginTop: 24 }}>If you ever want to come back, we'll be here. 🌿</p>
            </div>
          </div>
          <p style={s.footer}>thecura.app · Your feedback is private and used only to improve Cura</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.blob1} />
      <div style={s.blob2} />
      <div style={s.container}>
        <div style={s.logoWrap}>
          <img src="/logo.png" alt="Cura" style={s.logo} />
        </div>
        <div style={s.card}>
          <div style={s.stepHeader}>
            <div style={s.stepTag}>Feedback</div>
            <h1 style={s.title}>We're sorry to see you go</h1>
            <p style={s.sub}>What made you cancel? Select all that apply.</p>
          </div>

          {/* Reason chips */}
          <div style={s.chips}>
            {CANCELLATION_FEEDBACK_REASONS.map(r => {
              const active = selected.has(r.id);
              return (
                <button
                  key={r.id}
                  style={{ ...s.chip, ...(active ? s.chipActive : {}) }}
                  onClick={() => toggleReason(r.id)}
                >
                  {active && (
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ marginRight: 5, flexShrink: 0 }}>
                      <path d="M2 6.5L5.2 9.5L11 3" stroke="#1a3d35" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                  <span style={{ ...s.chipLabel, color: active ? '#1a3d35' : 'var(--text-secondary)' }}>{r.label}</span>
                </button>
              );
            })}
          </div>

          {/* Details field */}
          <div style={s.detailsHeader}>
            <span style={s.detailsLabel}>Tell us more</span>
            <span style={s.requiredBadge}>Required</span>
          </div>
          <textarea
            style={{
              ...s.textarea,
              borderColor: showDetailsError ? '#f87171' : 'rgba(36,85,76,0.12)',
              background: showDetailsError ? '#fff0f0' : 'rgba(245,252,240,0.7)',
            }}
            value={details}
            onChange={e => {
              setDetails(e.target.value);
              if (showDetailsError) setShowDetailsError(false);
            }}
            placeholder="What would have made you stay? Any specific features, issues, or feedback..."
            rows={4}
            maxLength={1500}
          />
          {showDetailsError && (
            <p style={s.errorText}>Please add some details before submitting.</p>
          )}

          <button
            style={{ ...s.primaryBtn, ...(canSubmit ? {} : s.primaryBtnDisabled) }}
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
        <p style={s.footer}>thecura.app · Your feedback is private and used only to improve Cura</p>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
    position: 'relative',
    overflow: 'hidden',
  },
  blob1: {
    position: 'fixed', top: '-10%', right: '-5%',
    width: 400, height: 400, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(160,230,160,0.25) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  blob2: {
    position: 'fixed', bottom: '-10%', left: '-5%',
    width: 350, height: 350, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(36,85,76,0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  container: {
    width: '100%', maxWidth: 520,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 20, position: 'relative', zIndex: 1,
  },
  logoWrap: { marginBottom: 4 },
  logo: { height: 36, objectFit: 'contain' },
  card: {
    width: '100%',
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(255,255,255,0.9)',
    boxShadow: '0 20px 60px rgba(36,85,76,0.12), 0 1px 0 rgba(255,255,255,0.8) inset',
    padding: '32px 32px 36px',
    overflow: 'hidden',
  },
  stepHeader: { marginBottom: 20 },
  stepTag: {
    display: 'inline-block',
    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
    color: 'var(--green-700)', background: 'var(--green-100)',
    padding: '4px 10px', borderRadius: 99,
    marginBottom: 12, textTransform: 'uppercase',
  },
  title: {
    fontSize: 22, fontWeight: 800, color: 'var(--text-primary)',
    lineHeight: 1.3, marginBottom: 8, letterSpacing: '-0.3px',
  },
  sub: { fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.65 },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 22,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '9px 14px',
    background: 'rgba(247,253,251,0.9)',
    border: '1px solid rgba(36,85,76,0.15)',
    borderRadius: 99,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontSize: 13, fontWeight: 600,
  },
  chipActive: {
    background: '#e2f5d3',
    borderColor: '#24554C',
    borderWidth: 1.5,
  },
  chipLabel: {
    fontSize: 13, fontWeight: 600,
  },
  detailsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  detailsLabel: {
    fontSize: 13, fontWeight: 700, color: 'var(--text-primary)',
  },
  requiredBadge: {
    fontSize: 10, fontWeight: 700,
    color: '#24554c', background: '#e2f5d3',
    padding: '2px 8px', borderRadius: 99,
  },
  textarea: {
    width: '100%', padding: '14px 16px',
    border: '1.5px solid rgba(36,85,76,0.12)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.65,
    resize: 'vertical', marginBottom: 6,
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  errorText: {
    fontSize: 12, color: '#f87171', fontWeight: 500, marginBottom: 14,
  },
  primaryBtn: {
    width: '100%', padding: '15px',
    background: 'linear-gradient(135deg, #1a3d35 0%, #24554c 50%, #3a8575 100%)',
    color: 'white', borderRadius: 'var(--radius-sm)',
    fontSize: 15, fontWeight: 700, letterSpacing: '0.01em',
    boxShadow: '0 4px 20px rgba(36,85,76,0.3)',
    transition: 'opacity 0.15s, transform 0.15s',
    marginTop: 14,
    border: 'none',
    cursor: 'pointer',
  },
  primaryBtnDisabled: { opacity: 0.35, boxShadow: 'none', cursor: 'default' },
  doneWrap: { padding: '16px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 },
  doneIconWrap: {
    width: 72, height: 72, borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--green-100), var(--green-200))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(36,85,76,0.15)',
    marginBottom: 8,
  },
  footer: {
    fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.6,
  },
};
